import {
  createContext, useContext, useEffect, useRef, useState, useCallback,
  type ReactNode,
} from 'react'
import { useOptionalAuth } from './AuthContext'
import { updateUser } from '../lib/api'
import { applyAccent, DEFAULT_ACCENT, type AccentKey } from '../lib/accents'

export type Appearance = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

interface ThemeContextValue {
  appearance: Appearance
  resolved: ResolvedTheme
  setAppearance: (next: Appearance) => Promise<void>
  /** The chosen accent preset. 'mono' is the house monochrome primary. */
  accent: AccentKey
  setAccent: (next: AccentKey) => Promise<void>
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

// Shared cache key so a cold page load can apply the right class before
// /auth/me returns. Cross-subdomain localStorage is per-host; the server is
// the source of truth — this cache only smooths the boot transition.
const STORAGE_KEY = 'meno_appearance'
// Set alongside STORAGE_KEY the moment someone taps the toggle ON THIS DEVICE.
// Its presence is what stops the server's copy from overriding a choice made
// here — see the sync effect below for the two ways that used to happen.
const CHOSEN_KEY = 'meno_appearance_chosen'
// Cached for the same reason as the appearance above: so a cold load paints in
// the chosen accent instead of flashing the monochrome default for the round
// trip it takes /auth/me to answer.
const ACCENT_KEY = 'meno_accent'

function readCachedAppearance(): Appearance {
  if (typeof window === 'undefined') return 'light'
  const raw = window.localStorage.getItem(STORAGE_KEY)
  return raw === 'dark' || raw === 'system' ? raw : 'light'
}

function readCachedAccent(): AccentKey {
  if (typeof window === 'undefined') return DEFAULT_ACCENT
  return (window.localStorage.getItem(ACCENT_KEY) as AccentKey | null) ?? DEFAULT_ACCENT
}

function resolveAppearance(a: Appearance): ResolvedTheme {
  if (a === 'system') {
    if (typeof window === 'undefined') return 'light'
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return a
}

// Toggle on <html>. Pair `dark` with `admin-dark` so the brand-black palette
// (cards, muted surfaces, escape-hatch utilities like .admin-dark .bg-white)
// activates alongside the standard shadcn `dark` variables.
function applyClass(resolved: ResolvedTheme) {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (resolved === 'dark') {
    root.classList.add('dark', 'admin-dark')
    root.style.colorScheme = 'dark'
  } else {
    root.classList.remove('dark', 'admin-dark')
    root.style.colorScheme = 'light'
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // Optional: on the public Ping storefront there's no AuthProvider — fall back
  // to null (→ localStorage/system appearance) instead of throwing.
  const user = useOptionalAuth()?.user ?? null
  const [appearance, setAppearanceState] = useState<Appearance>(readCachedAppearance)
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolveAppearance(readCachedAppearance()))
  const [accent, setAccentState] = useState<AccentKey>(readCachedAccent)

  // Apply the cached class synchronously on first render so the page paints
  // in the right mode from the start, without waiting for the appearance effect.
  useEffect(() => {
    applyClass(resolveAppearance(readCachedAppearance()))
    applyAccent(readCachedAccent())
    // Run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync from /auth/me ONLY on the first time the user lands. Subsequent
  // user updates (e.g. proactive auth refresh returning stale appearance)
  // must not clobber a choice the user just made in this session.
  //
  // And never over a choice made on this device. Two things made the toggle
  // "not work" on a phone while working on every desktop:
  //   1. /auth/me resolves later on a phone network, so a tap that landed
  //      before it did was overridden a second later by the server's copy;
  //   2. a device whose session cannot save to the server (a paired till, a
  //      403 on the user record) kept the class locally — then the PWA was
  //      backgrounded, killed, reopened, and the cold-start sync put the
  //      server's stale value back. Every reopen undid the tap.
  // A device that has chosen keeps its choice and pushes it up instead.
  const hasSyncedFromServer = useRef(false)
  useEffect(() => {
    if (!user || hasSyncedFromServer.current) return
    hasSyncedFromServer.current = true
    const fromServer = (user as any)?.appearance as Appearance | undefined
    const chosenHere = window.localStorage.getItem(CHOSEN_KEY) === '1'
    if (chosenHere) {
      if (fromServer && fromServer !== appearance && user.id) {
        updateUser(user.id, { appearance }).catch(() => { /* best-effort */ })
      }
    } else if (fromServer && fromServer !== appearance) {
      setAppearanceState(fromServer)
      window.localStorage.setItem(STORAGE_KEY, fromServer)
    }
    // NULL on the server means "never chosen", which must not overwrite a
    // choice made on this device before the user ever signed in here.
    const accentFromServer = (user as any)?.accent as AccentKey | null | undefined
    if (accentFromServer) {
      setAccentState(accentFromServer)
      window.localStorage.setItem(ACCENT_KEY, accentFromServer)
      applyAccent(accentFromServer)
    }
  // appearance intentionally excluded; only react to user becoming non-null.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  // Apply the resolved class on every appearance change.
  useEffect(() => {
    const next = resolveAppearance(appearance)
    setResolved(next)
    applyClass(next)
  }, [appearance])

  // For `system`, follow the OS scheme changes live.
  useEffect(() => {
    if (appearance !== 'system' || typeof window === 'undefined') return
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = () => {
      const next: ResolvedTheme = mq.matches ? 'dark' : 'light'
      setResolved(next)
      applyClass(next)
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [appearance])

  const setAppearance = useCallback(async (next: Appearance) => {
    setAppearanceState(next)
    window.localStorage.setItem(STORAGE_KEY, next)
    window.localStorage.setItem(CHOSEN_KEY, '1')
    // A tap that beats /auth/me is still the user's decision: the sync that
    // follows must not treat the server's older value as newer.
    hasSyncedFromServer.current = true
    // Apply immediately too — don't wait for the React effect, so the user
    // sees the page flip on click instead of one render-tick later.
    applyClass(resolveAppearance(next))
    if (user?.id) {
      try {
        await updateUser(user.id, { appearance: next })
      } catch {
        // Non-blocking: the class is already applied locally; the next
        // /auth/me will reconcile if the save failed.
      }
    }
  }, [user?.id])

  const setAccent = useCallback(async (next: AccentKey) => {
    setAccentState(next)
    window.localStorage.setItem(ACCENT_KEY, next)
    // Applied before the request, not after: this is a colour change someone
    // is watching for, and a round trip between the click and the paint reads
    // as the control being broken.
    applyAccent(next)
    if (user?.id) {
      try {
        await updateUser(user.id, { accent: next } as any)
      } catch {
        // Same trade as the appearance above — the local paint already
        // happened, and the next /auth/me reconciles a failed save.
      }
    }
  }, [user?.id])

  return (
    <ThemeContext.Provider value={{ appearance, resolved, setAppearance, accent, setAccent }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used inside ThemeProvider')
  return ctx
}
